import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
  Skeleton,
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';

function formatPostedDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diffDays = Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function JobCard({ job, onApply }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid #E5E7EB',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(30,58,138,0.12)',
          borderColor: '#BFDBFE',
        },
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ lineHeight: 1.3, flex: 1, mr: 1 }}
          >
            {job.title}
          </Typography>
          <WorkOutlineIcon sx={{ color: '#1E3A8A', fontSize: 20, flexShrink: 0 }} />
        </Box>

        {job.company_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <BusinessOutlinedIcon sx={{ fontSize: 14, color: '#64748B' }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {job.company_name}
            </Typography>
          </Box>
        )}

        {job.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 14, color: '#64748B' }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {job.location}
            </Typography>
          </Box>
        )}

        {(job.salary || job.stipend) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AttachMoneyOutlinedIcon sx={{ fontSize: 14, color: '#64748B' }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {job.salary || job.stipend}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {job.job_type && (
            <Chip
              label={job.job_type}
              size="small"
              sx={{
                fontSize: '0.68rem',
                height: 20,
                bgcolor: '#EEF2FF',
                color: '#1E3A8A',
                fontWeight: 600,
              }}
            />
          )}
          {job.work_mode && (
            <Chip
              label={job.work_mode}
              size="small"
              sx={{
                fontSize: '0.68rem',
                height: 20,
                bgcolor: '#F0FDF4',
                color: '#16A34A',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {job.created_at && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
            <Typography variant="caption" color="text.disabled">
              Posted {formatPostedDate(job.created_at)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={() => onApply(job.id)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.78rem',
            background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)',
            },
          }}
        >
          Apply Now
        </Button>
        <Button
          variant="outlined"
          size="small"
          title="Save Job"
          sx={{
            borderRadius: 2,
            minWidth: 36,
            px: 0.5,
            borderColor: '#E2E8F0',
            color: '#64748B',
            '&:hover': { borderColor: '#1E3A8A', color: '#1E3A8A' },
          }}
          onClick={() => onApply(job.id)}
        >
          <BookmarkBorderIcon sx={{ fontSize: 18 }} />
        </Button>
      </Box>
    </Paper>
  );
}

function RecommendedJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api
      .get('/candidate/recommended-jobs?limit=4')
      .then((res) => setJobs(res.data.recommendedJobs || []))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  if (failed) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
          <Typography
              variant="h2"
              fontWeight="bold"
              sx={{
                  fontSize: "1.5rem", // Same as "Quick Actions"
              }}
          >
              Recommended Jobs For You
          </Typography>
        <Button
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/jobs')}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#1E3A8A' }}
        >
          View All
        </Button>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : jobs.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid #E5E7EB',
            background: '#FAFAFA',
          }}
        >
          <WorkOutlineIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No recommended jobs at the moment. Check back soon!
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/jobs')}
            sx={{
              textTransform: 'none',
              borderColor: '#1E3A8A',
              color: '#1E3A8A',
              '&:hover': { bgcolor: '#EEF2FF' },
            }}
          >
            Browse All Jobs
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {jobs.map((job) => (
            <Grid item xs={12} sm={6} md={3} key={job.id}>
              <JobCard job={job} onApply={(id) => navigate(`/jobs/${id}`)} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default RecommendedJobs;
